import {
    EVERY_MINUTE,
    logger,
    PAGES,
    useNetworkState,
} from '@register-ui/utils';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { validateDeviceTime } from './utils';

const VALIDATION_INTERVAL = 10 * EVERY_MINUTE;
const INITIAL_VALIDATION_DELAY_MS = 1500;

interface Display {
    device: string;
    server: string;
}

const URLS_TO_VALIDATE = [PAGES.CHECKOUT];

export function useDeviceTimeGuard() {
    const [shouldBlock, setShouldBlock] = useState(false);
    const { pathname } = useLocation();

    const [display, setDisplay] = useState<Display>();

    const { isOnline } = useNetworkState();

    const runTimeChecks = useCallback(async () => {
        if (!isOnline) {
            return;
        }

        try {
            const { shouldBlockUser, timeInfo } = await validateDeviceTime();

            setShouldBlock(shouldBlockUser);

            if (timeInfo) {
                setDisplay(timeInfo);
            }
        } catch {
            logger.error('Unable to validate device time');
        }
    }, [isOnline]);

    useEffect(() => {
        const initialTimeout = setTimeout(() => {
            runTimeChecks();
        }, INITIAL_VALIDATION_DELAY_MS);

        const interval = setInterval(() => {
            runTimeChecks();
        }, VALIDATION_INTERVAL);

        return () => {
            clearTimeout(initialTimeout);
            clearInterval(interval);
        };
    }, [isOnline, runTimeChecks]);

    useEffect(() => {
        if (URLS_TO_VALIDATE.includes(pathname)) {
            runTimeChecks();
        }
    }, [pathname, runTimeChecks]);

    return { hasWrongTimeSettings: shouldBlock, display };
}
